import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { SearchSettingsCard } from '../components/search-settings-card';
import { StoreSettingsForm } from '../components/store-settings-form';
import { StoreSettingsHub } from '../components/store-settings-hub';
import { StoreStatusActions } from '../components/store-status-actions';
import { StoreStatusBadge } from '../components/store-status-badge';
import { ViewOnStorefrontButton } from '../components/view-on-storefront-button';
import { useStoreDetail } from '../hooks/use-stores';
import { STORE_PAGE_TABS, type StorePageTab } from '../schemas/store-page-search';

const ROUTE = '/_authed/_active/org/stores/$storeId' as const;

function isTab(value: string): value is StorePageTab {
  return (STORE_PAGE_TABS as readonly string[]).includes(value);
}

export interface StoreSettingsPageProps {
  /** Rendered above the settings hub, e.g. the launch checklist the route composes in. */
  hubAside?: ReactNode;
}

function StoreSettings({ hubAside }: StoreSettingsPageProps) {
  const { t } = useTranslation('stores');
  const { storeId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: '/org/stores/$storeId' });
  // Prefetched by the route loader.
  const { data: store } = useStoreDetail(storeId);
  // The backend PATCH needs both permissions for profile fields and refuses a closed store outright.
  const details = useStoreEditable({
    store: store ?? null,
    permission: ['stores.write', 'settings.write'],
  });
  if (!store) return null;
  const tab = search.tab ?? 'settings';

  return (
    <PageContent>
      <PageHeader
        title={store.name}
        meta={<StoreStatusBadge status={store.status} />}
        description={
          <>
            <span className="block">{`${store.slug} · ${store.defaultCurrency} · ${store.timezone}`}</span>
            <span className="block">{t(`status.hint.${store.status}`)}</span>
          </>
        }
        backTo={{ to: '/org/stores' }}
        backLabel={t('title')}
        actions={
          <>
            <ViewOnStorefrontButton
              url={store.storefrontUrl}
              live
              label={t('storefront.open')}
              size="default"
            />
            <Can permission="stores.write">
              <StoreStatusActions store={store} />
            </Can>
          </>
        }
      />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (isTab(value)) void navigate({ search: (prev) => ({ ...prev, tab: value }) });
        }}
        className="gap-6"
      >
        <TabsList aria-label={t('tabs.label')} data-tour="settings.hub.tabs">
          <TabsTrigger value="settings">{t('tabs.settings')}</TabsTrigger>
          <TabsTrigger value="details">{t('tabs.details')}</TabsTrigger>
        </TabsList>
        <TabsContent value="settings" className="flex flex-col gap-6">
          <ReadOnlyBanner reason={store.status === 'closed' ? 'closed' : null} className="mb-0" />
          {hubAside}
          <StoreSettingsHub storeId={store.id} />
        </TabsContent>
        {/* Stays mounted so unsaved edits survive a look at the settings tab. */}
        <TabsContent
          value="details"
          forceMount
          hidden={tab !== 'details'}
          className="flex flex-col gap-6"
        >
          <ReadOnlyBanner reason={details.reason} className="mb-0" />
          <StoreSettingsForm key={store.updatedAt} store={store} readOnly={!details.editable} />
          <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
            <Can permission="products.write">
              <SearchSettingsCard storeId={store.id} />
            </Can>
          </div>
        </TabsContent>
      </Tabs>
    </PageContent>
  );
}

export function StoreSettingsPage(props: StoreSettingsPageProps) {
  return (
    <FeatureErrorBoundary name="stores.settings">
      <StoreSettings {...props} />
    </FeatureErrorBoundary>
  );
}
